import type { ChatMessage as ChatMessageType } from "schemas/generated/chatMessage";

import React, { useState } from "react";

import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { useErrorToast } from "@editor/hooks/useErrorToast";
import { useLogAnalytics } from "@editor/hooks/useLogAnalytics";
import { chatRouter, chatUtils } from "@editor/trpc/chat";
import { writeHtmlToClipboard } from "@editor/utils/copyPaste";

import { toast } from "@replo/design-system/components/alert/Toast";
import IconButton from "@replo/design-system/components/button/IconButton";
import twMerge from "@replo/design-system/utils/twMerge";
import { BadgeCheck, Copy, ThumbsDown, ThumbsUp } from "lucide-react";

import { useChatId } from "./hooks/useChatId";
import { markdownToHtml } from "./utils/markdownToHtml";

interface ChatActionsProps {
  messages: ChatMessageType[];
  role: "user" | "assistant";
  isChatLoading?: boolean;
  isLastAssistantGroup?: boolean;
}

const THINK_TAG_REGEX = /<think>([\S\s]*?)<\/think>/g;

type TextContentItem = Extract<
  ChatMessageType["content"][number],
  {
    type: "text";
  }
>;

const buildClipboardPayload = (messages: ChatMessageType[]) => {
  const htmlParts: string[] = [];

  for (const message of messages) {
    const textItems = message.content.filter(
      (item): item is TextContentItem => item.type === "text",
    );

    for (const item of textItems) {
      const cleaned = item.text.replace(THINK_TAG_REGEX, "").trim();
      if (!cleaned) {
        continue;
      }

      const html = markdownToHtml(cleaned);
      htmlParts.push(html);
    }
  }

  return {
    html: htmlParts.join(""),
  };
};

export const ChatActions: React.FC<ChatActionsProps> = ({
  messages,
  role,
  isChatLoading = false,
  isLastAssistantGroup = false,
}) => {
  const chatId = useChatId();
  const logEvent = useLogAnalytics();
  const errorToast = useErrorToast();

  const projectId = useCurrentProjectId();
  const [isCopied, setIsCopied] = useState(false);
  const [optimisticFeedback, setOptimisticFeedback] = useState<
    "positive" | "negative" | "none" | null
  >(null);

  const actualFeedback =
    messages.find((message) => message.feedback !== null)?.feedback ?? null;

  const currentFeedback = optimisticFeedback ?? actualFeedback;

  const { mutate: submitFeedback } = chatRouter.submitFeedback.useMutation({
    onSuccess: () => {
      if (chatId) {
        void chatUtils.getPaginatedMessages.invalidate({
          chatId,
          projectId,
        });
      }
    },
    onError: () => {
      setOptimisticFeedback(null);
      toast({
        header: "Failed to submit feedback",
        message: "Please try again.",
      });
    },
  });

  if (isLastAssistantGroup && isChatLoading) {
    return null;
  }

  const extractLLMRequestId = (messages: ChatMessageType[]): string | null => {
    for (const message of messages) {
      for (const contentItem of message.content) {
        if (contentItem.llmRequestId) {
          return contentItem.llmRequestId;
        }
      }
    }
    return null;
  };

  const handleCopy = async () => {
    try {
      const { html } = buildClipboardPayload(messages);

      if (!html) {
        return;
      }

      await writeHtmlToClipboard(html, errorToast);

      setIsCopied(true);
      toast({
        header: "Message copied to clipboard",
      });

      setTimeout(() => {
        setIsCopied(false);
      }, 2000);
    } catch (error) {
      console.error("Failed to copy message:", error);
    }
  };

  const handleFeedback = (rating: "positive" | "negative" | "none") => {
    logEvent("chat.feedback", {
      rating,
      chatId: chatId ?? "",
    });

    const llmRequestId = extractLLMRequestId(messages);
    if (!llmRequestId || !projectId) {
      return;
    }

    const actualCurrentFeedback =
      messages.find((message) => message.feedback !== null)?.feedback ?? null;
    const newRating = actualCurrentFeedback === rating ? "none" : rating;

    setOptimisticFeedback(newRating);

    submitFeedback({
      projectId,
      llmRequestId,
      rating: newRating,
      messageIds: messages.map((message) => message.id),
    });
  };

  return (
    <div
      className={twMerge(
        "flex flex-row gap-1 transition-opacity mt-1",
        role === "assistant" && "pl-2",
      )}
    >
      {role === "assistant" && (
        <>
          <IconButton
            variant="tertiary"
            size="sm"
            tooltipText="Good response"
            onClick={() => handleFeedback("positive")}
            icon={
              <ThumbsUp
                size={14}
                fill={currentFeedback === "positive" ? "currentColor" : "none"}
                stroke="currentColor"
              />
            }
          />
          <IconButton
            variant="tertiary"
            size="sm"
            tooltipText="Poor response"
            onClick={() => handleFeedback("negative")}
            icon={
              <ThumbsDown
                size={14}
                fill={currentFeedback === "negative" ? "currentColor" : "none"}
                stroke="currentColor"
              />
            }
          />
        </>
      )}
      <IconButton
        variant="tertiary"
        size="sm"
        tooltipText="Copy message"
        onClick={handleCopy}
        icon={
          isCopied ? (
            <BadgeCheck size={14} className="text-success" />
          ) : (
            <Copy size={14} />
          )
        }
      />
    </div>
  );
};
